import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip
} from '@mui/material'
import {
  Content,
  ContentNuggetDataFragment,
  ContentNuggetDataFragmentDoc,
  TourPoiQuery,
  useDeleteContentMutation,
  useReorderContentMutation
} from '@typings/graphql'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'
import ListIcon from '@mui/icons-material/List'
import DeleteIcon from '@mui/icons-material/Delete'
import Grid3x3Icon from '@mui/icons-material/Grid3x3'
import { ConfirmDialog } from '@features/cms/components/dialogs/ConfirmDialog'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import {
  restrictToVerticalAxis,
  restrictToFirstScrollableAncestor
} from '@dnd-kit/modifiers'
import { CSS } from '@dnd-kit/utilities'
import { useFragment } from '@apollo/client'
import { apolloClient } from '@services/apollo'
import { updateContent } from '@features/cms/components/content-type-editors/mutation-helper/content'
import { CopyEntityButton } from '@features/cms/components/copy/CopyEntityButton'

import { EditContentPanel } from './EditContentPanel'
import { CreateContentDialog } from './dialogs/CreateContentDialog'

/** Content types that act as quiz gates and should auto-block the following content. */
const QUIZ_TYPES = ['quiz', 'videoQuiz', 'multipleChoicePoll', 'poll']

/** Converts the texts JSON object from the Apollo cache to the array format required by UpdateContentInput. */
const textsToArray = (texts: any): { key: string; value: string }[] =>
  Object.entries(texts || {}).map(([key, value]) => ({ key, value: String(value) }))

type ItemProps = {
  content: TourPoiQuery['tourPoi']['sections'][0]['nuggets'][0]['content'][number];
  nuggetId: string;
  selected: boolean;
  onDelete: () => void;
  onClick: () => void;
};

const ContentNuggetListItem: React.FC<ItemProps> = ({
  content,
  nuggetId,
  selected,
  onDelete,
  onClick
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition
  } = useSortable({ id: content.id })

  const { t } = useTranslation()

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }


  return (
    <div ref={setNodeRef} id={`content-item-${content.id}`} style={style} {...attributes}>
      <ListItem
        key={content.id}
        sx={{
          opacity: content.hidden ? 0.3 : 1,
          '& .MuiListItemSecondaryAction-root': {
            right: 0,
            opacity: 0,
            transition: 'opacity 0.3s ease-in-out',
            px: 0.5,
            backgroundColor: '#ddd9',
            borderRadius: 2,
            mr: 0.5
          },
          '& .MuiListItemIcon-root': {
            minWidth: 0
          },
          color: selected ? 'primary.main' : 'inherit',
          ':hover': {
            '& .MuiListItemSecondaryAction-root': {
              opacity: 1
            }
          }
        }}
        secondaryAction={
          <Box
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Tooltip title={t('common.copyId')}>
              <Button
                variant="text"
                size="small"
                sx={{ p: 1, minWidth: 0, color: '#666' }}
                onClick={() => navigator.clipboard.writeText(content.id)}
              >
                <Grid3x3Icon fontSize="small" sx={{ fontSize: 16 }} />
              </Button>
            </Tooltip>
            <CopyEntityButton
              level="content"
              sourceId={content.id}
              currentNuggetId={nuggetId}
            />
            <Tooltip title={t('common.delete')}>
              <Button
                variant="text"
                size="small"
                sx={{ p: 1, minWidth: 0, color: '#666' }}
                onClick={onDelete}
              >
                <DeleteIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title={t('common.move')} placement="right">
              <ListIcon
                ref={setActivatorNodeRef as any}
                {...listeners}
                sx={{ cursor: 'move', color: '#666' }}
              />
            </Tooltip>
          </Box>
        }
        disablePadding
      >
        <ListItemButton onClick={onClick}>
          <ListItemText
            sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', pr: 4 }}
            id={content.id}
            primary={`${content.order}. ${content.type}`}
          />
        </ListItemButton>
      </ListItem>
    </div>
  )
}

const PoiNuggetContent: React.FC = () => {
  const { t } = useTranslation()
  const { modeId, nuggetId, contentId } = useParams()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [selectedContent, setSelectedContent] = React.useState(contentId)
  const [showDialog, setShowDialog] = React.useState(false)
  const [contentIdToDelete, setContentIdToDelete] = useState('')
  const [deleteContent] = useDeleteContentMutation()
  const [reorderContent] = useReorderContentMutation()
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )
  const hasUnsavedChanges = useRef(false)
  /** Stores the last content item before the "Add content" dialog opens, used to auto-set blockedById. */
  const lastContentBeforeCreation = useRef<typeof contents[0] | null>(null)

  const contentNugget = useFragment<ContentNuggetDataFragment>({
    fragment: ContentNuggetDataFragmentDoc,
    fragmentName: 'ContentNuggetData',
    from: {
      id: nuggetId as string,
      __typename: 'ContentNugget'
    }
  })

  const nugget = contentNugget.data as ContentNuggetDataFragment
  /*
  const { data, refetch } = useTourPoiQuery({
    variables: { id: poiId as string },
    fetchPolicy: 'network-only'
  })
  const poi = useMemo(() => data?.tourPoi, [data])

  const nugget = useMemo(
    () =>
      poi?.sections
        .find((s) => s.id === sectionId)
        ?.nuggets.find((n) => n.id === nuggetId),
    [poi, sectionId, nuggetId]
  )
 */
  const contents = useMemo(() => {
    if (!nugget.content) {
      return []
    }

    const arr = [...nugget.content]
    arr.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))

    if (!selectedContent) {
      setSelectedContent(arr[0]?.id)
    }

    return arr
  }, [nugget])

  const selectContent = (id: string) => {
    if (hasUnsavedChanges.current) {
      if (
        window.confirm(
          'Möchtest du wirklich die Seite wechseln? Deine Änderungen gehen dadurch verloren.'
        )
      ) {
        setSelectedContent(id)
      }
    } else {
      setSelectedContent(id)
    }
  }

  const onConfirmDelete = useCallback(async () => {
    try {
      await deleteContent({
        variables: {
          id: contentIdToDelete
        },
        update: (cache) => {
          cache.evict({
            id: `Content:${contentIdToDelete}`
          })
        }
      })

      setSelectedContent(undefined)
      setContentIdToDelete('')
    } catch (e) {
      console.log(e)
    }
  }, [contentIdToDelete])

  const listItems = useMemo(
    () =>
      contents.map((content) => {
        return (
          <ContentNuggetListItem
            key={content.id}
            nuggetId={nuggetId as string}
            content={content}
            selected={content.id === selectedContent}
            onClick={() => selectContent(content.id)}
            onDelete={() => setContentIdToDelete(content.id)}
          />
        )
      }),
    [contents, selectedContent]
  )

  const contentPanel = useMemo(() => {
    if (!nugget) {
      return null
    }

    const blocksContent =
      !!contents.find((c) => c.blockedById === selectedContent) ||
      !!contents.find((c) => c.id === selectedContent)?.blockedById

    return (
      <EditContentPanel
        nuggetId={nugget.id}
        contentId={selectedContent}
        blocksContent={blocksContent}
        // content={contents.find((c) => c.id === selectedContent)}
        modeId={modeId as string}
        refetch={() => {}}
        onEdited={(value) => {
          hasUnsavedChanges.current = value
        }}
      />
    )
  }, [selectedContent, contents])

  useEffect(() => {
    if (!selectedContent) {
      return
    }
    hasUnsavedChanges.current = false

    const parts = pathname.split('/')
    if (!contentId) {
      parts.push('')
    }
    parts[parts.length - 1] = selectedContent

    navigate(parts.join('/').replace('//', '/'), { replace: true })
  }, [selectedContent, contentId, pathname])

  // Deep-link from the tour search: bring the targeted content item into view.
  useEffect(() => {
    if (!contentId) {
      return
    }

    const handle = setTimeout(() => {
      document
        .getElementById(`content-item-${contentId}`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, 100)

    return () => clearTimeout(handle)
  }, [contentId])

  const onContentCreated = async (id: string) => {
    setShowDialog(false)

    const predecessor = lastContentBeforeCreation.current
    lastContentBeforeCreation.current = null

    if (predecessor && QUIZ_TYPES.includes(predecessor.type)) {
      // Read the freshly created content from the Apollo cache (the createContent mutation
      // already wrote it there via the update callback before onCreated was called).
      const nuggetData = apolloClient.readFragment<ContentNuggetDataFragment>({
        id: `ContentNugget:${nuggetId}`,
        fragment: ContentNuggetDataFragmentDoc,
        fragmentName: 'ContentNuggetData'
      })
      const newContent = nuggetData?.content?.find((c) => c.id === id)
      if (newContent && !newContent.blockedById) {
        await updateContent(id, {
          nuggetId: nuggetId!,
          order: newContent.order,
          type: newContent.type,
          texts: textsToArray(newContent.texts),
          blockedById: predecessor.id
        })
      }
    }

    setSelectedContent(id)
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!active || !over) {
      return
    }

    if (active.id !== over.id) {
      const ids = contents.map((item) => item.id)
      const oldIndex = ids.indexOf(active.id as string)
      const newIndex = ids.indexOf(over.id as string)
      const newIds = arrayMove(ids, oldIndex, newIndex)

      // Snapshot both orderings before the mutation updates the cache.
      const prevContents = [...contents]
      const newOrderedContents = newIds.map((id) => prevContents.find((c) => c.id === id)!)

      await reorderContent({
        variables: {
          ids: newIds
        },
        optimisticResponse: {
          __typename: 'Mutation',
          reorderContents: newIds.map((id, index) => ({
            __typename: 'Content',
            ...prevContents.find((c) => c.id === id) as Content,
            order: index + 1
          }))
        }
      })

      // Auto-update blockedById for any item whose predecessor changed relative to a quiz.
      const blockerUpdates: Promise<any>[] = []

      newOrderedContents.forEach((content, newIdx) => {
        const prevIdx = prevContents.findIndex((c) => c.id === content.id)
        const prevPredecessor = prevIdx > 0 ? prevContents[prevIdx - 1] : null
        const newPredecessor = newIdx > 0 ? newOrderedContents[newIdx - 1] : null

        // Skip if the predecessor didn't change.
        if (prevPredecessor?.id === newPredecessor?.id) return

        const prevPredIsQuiz = prevPredecessor && QUIZ_TYPES.includes(prevPredecessor.type)
        const newPredIsQuiz = newPredecessor && QUIZ_TYPES.includes(newPredecessor.type)

        if (!prevPredIsQuiz && !newPredIsQuiz) return

        let newBlockedById: string | null = content.blockedById ?? null

        if (newPredIsQuiz && (!content.blockedById || content.blockedById === prevPredecessor?.id)) {
          // Moved under a quiz: set blocker (only if not manually overridden).
          newBlockedById = newPredecessor!.id
        } else if (prevPredIsQuiz && !newPredIsQuiz && content.blockedById === prevPredecessor!.id) {
          // Moved away from its auto-assigned quiz: clear the blocker.
          newBlockedById = null
        }

        if (newBlockedById !== (content.blockedById ?? null)) {
          // Use newIdx + 1 as the order — matching what reorderContents assigned —
          // so this update does not accidentally reset the content's position.
          blockerUpdates.push(
            updateContent(content.id, {
              nuggetId: nuggetId!,
              order: newIdx + 1,
              type: content.type,
              texts: textsToArray(content.texts),
              blockedById: newBlockedById
            })
          )
        }
      })

      await Promise.all(blockerUpdates)
    }
  }

  return (
    <>
      <Box display="flex" flex={1} overflow="hidden">
        <Box
          display="flex"
          flexDirection="column"
          sx={{
            height: '100%',
            width: '250px',
            flex: '0 0 250px',
            borderRight: '1px solid #ccc'
          }}
        >
          <Box flex={1} overflow="auto">
            {!listItems.length
              ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="#666"
                  mt={2}
                >
                  <ListIcon sx={{ mr: 1 }} />
                  {t('edit.poi.noContent')}
                </Box>
                )
              : (
                <List sx={{ p: 0 }}>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    modifiers={[
                      restrictToVerticalAxis,
                      restrictToFirstScrollableAncestor
                    ]}
                  >
                    <SortableContext
                      items={contents.map((item) => item.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {listItems}
                    </SortableContext>
                  </DndContext>
                </List>
                )}
          </Box>
          <Box p={2} mt={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              sx={{ color: '#fff', width: '100%' }}
              onClick={() => {
                lastContentBeforeCreation.current = contents[contents.length - 1] ?? null
                setShowDialog(true)
              }}
            >
              {t('edit.poi.addContent')}
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'hidden'
          }}
        >
          {contentPanel}
        </Box>
      </Box>

      <CreateContentDialog
        modeId={modeId as string}
        nuggetId={nuggetId as string}
        show={showDialog}
        onClose={() => setShowDialog(false)}
        onCreated={onContentCreated}
      />

      <ConfirmDialog
        open={!!contentIdToDelete}
        text={t('edit.content.confirmDeleteContent')}
        onConfirm={onConfirmDelete}
        onCancel={() => setContentIdToDelete('')}
      />
    </>
  )
}

export default PoiNuggetContent
